import React from 'react'

import { Steps as AntSteps, Button, Divider, Space, Alert } from 'antd';
import { getConvertedCatalogItems, flattenItem, merge, updateSelected, mapAttributeValuesToLabels } from './stepsModel'
import API from '../api'
import Import from './Import.jsx'
import NoMatch from './NoMatch.jsx'
import Preview from './Preview.jsx'
import ImportModifiers from './ImportModifiers.jsx'
import { UploadFeedback } from './UploadFeedback'
import { checkForDuplicates } from './importModel';

export default function Steps({ finish = () => { }, openCounter }) {
  const [currentStep, setCurrentStep] = React.useState(0)
  const [uploadedData, setUploadedData] = React.useState([])
  const [items, setItems] = React.useState([])
  const [importModifiers, setImportModifiers] = React.useState({ type: 'name', attributes: ['name', 'sku'] })
  const [selectedNewItems, setSelectedNewItems] = React.useState([])
  const [prepareForInputData, setPrepareForInputData] = React.useState({ newItems: [], matches: [] })

  const defaultUploadFeedbackProps = { state: 'uploading', numberUpdated: 0, attributesUpdated: [] }
  const [uploadFeedbackProps, setUploadFeedbackProps] = React.useState(defaultUploadFeedbackProps)

  React.useEffect(() => {
    (async () => {
      if (currentStep === 0) {
        const items = await API.get("/items", {})
        setItems(getConvertedCatalogItems(items.data))
      }
    })()
  }, [openCounter, currentStep])

  const next = () => {
    setCurrentStep(currentStep + 1)
  }

  const skipNext = () => {
    setCurrentStep(currentStep + 2)
  }

  const mergeAndNext = () => {
    const { newItems, matches } = merge(importModifiers.type, importModifiers.attributes, items, uploadedData)
    setPrepareForInputData({ newItems, matches })

    if (newItems.length <= 0) {
      skipNext()
    } else {
      next()
    }
  }

  const prev = () => {
    setCurrentStep(currentStep - 1);
  };

  const upload = async () => {
    next()

    const body = {
      updated: prepareForInputData.matches.map(x => flattenItem(importModifiers.attributes, x)),
      new: prepareForInputData.newItems.filter(x => x.selected).map(x => {
        // Removed ui state from the object sent in the body
        const desiredProperties = x;
        delete desiredProperties['new']
        delete desiredProperties['selected']
        return desiredProperties
      }),
      updatedFields: importModifiers.attributes
    }

    try {
      const response = await API.post("/web/bulk-items", body)
      setUploadFeedbackProps({
        state: 'success',
        numberUpdated: response.data.numberOfItemsUpdated + response.data.numberOfNewItems,
        attributesUpdated: mapAttributeValuesToLabels(response.data.updatedFields)
      })
    }
    catch (ex) {
      setUploadFeedbackProps({
        state: 'error'
      })
    }
  }

  const done = () => {
    finish()
    resetState()
  }

  const resetState = () => {
    setCurrentStep(0)
    setUploadedData([])
    setImportModifiers({ type: 'name', attributes: ['name', 'sku'] })
    setSelectedNewItems([])
    setPrepareForInputData({ newItems: [], matches: [] })
    setUploadFeedbackProps(defaultUploadFeedbackProps)
  }

  const uploaded = uploadedData => {
    setUploadedData(uploadedData)
    next()
  }

  const updateImportModifiers = importModifiers => {
    setImportModifiers(importModifiers)
  }

  const updateSelectedMatches = selectedMatches => {
    const newItems = updateSelected(importModifiers.type, prepareForInputData.newItems, selectedMatches)
    prepareForInputData.newItems = newItems
    setSelectedNewItems(selectedMatches)
  }

  const duplicates = checkForDuplicates(importModifiers.type, prepareForInputData.newItems)

  const steps = [
    {
      title: 'Select File',
      content: <Import uploaded={uploaded} />
    },
    {
      title: 'Options',
      content: <ImportModifiers
        data={uploadedData}
        selectedAttributes={importModifiers.attributes}
        updateImportModifiers={updateImportModifiers}
      />,
      buttons: { previous: true, mergeAndNext: true }
    },
    {
      title: 'New Items',
      content: <NoMatch
        rowKey={importModifiers.type}
        attributes={importModifiers.attributes}
        dataSource={prepareForInputData.newItems}
        selectedMatches={selectedNewItems}
        updateSelectedMatches={updateSelectedMatches}
        duplicates={duplicates}
      />,
      buttons: { previous: true, next: duplicates.length === 0 }
    },
    {
      title: 'Preview',
      content: <Preview
        rowKey={importModifiers.type}
        attributes={importModifiers.attributes}
        dataSource={[...selectedNewItems, ...prepareForInputData.matches]}
      />,
      buttons: { previous: true, confirm: true }
    },
    {
      title: 'Upload',
      content: <UploadFeedback {...uploadFeedbackProps} />,
      buttons: { done: true }
    }
  ];

  const hasItemsToUpload = () => selectedNewItems.length || prepareForInputData.matches.length


  return (
    <>
      <Space direction='vertical' size='large' style={{ width: '100%' }}>
        <AntSteps current={currentStep} items={steps}>
        </AntSteps>
        {steps[currentStep].content}
      </Space>
      {steps[currentStep].buttons && (
        <>
          <Divider />
          <Space>
            {steps[currentStep].buttons.previous && (
              <Button onClick={() => prev()}>
                Previous
              </Button>
            )}
            {steps[currentStep].buttons.confirm && <>
              <Button type="primary" onClick={() => upload()} disabled={!hasItemsToUpload()}>
                Confirm Import
              </Button>
              {!hasItemsToUpload() && <Alert type="warning" showIcon={true} message="No items to upload." />}
            </>}
            {steps[currentStep].buttons.mergeAndNext && (
              <Button type="primary" onClick={() => mergeAndNext()}>
                Next
              </Button>
            )
            }
            {steps[currentStep].buttons.next && (
              <Button type="primary" onClick={() => next()}>
                Next
              </Button>
            )}
            {steps[currentStep].buttons.done && (
              <Button type="primary" onClick={() => done()} disabled={uploadFeedbackProps.state === 'uploading'}>
                Done
              </Button>
            )}
          </Space>
        </>
      )}
    </>
  )
}
